import { Helmet } from '@dr.pogodin/react-helmet';
import { ChatIcon } from '@phosphor-icons/react';
import * as React from 'react';
import { useParams } from 'react-router-dom';
import { css, html } from 'react-strict-dom';

import { Api } from '../../../api';
import {
  Avatar,
  Button,
  DateWithTooltip,
  PageError,
  PageHeader,
  PageLoading,
  PageWrap,
  PageWrapPadding,
  Stack,
  Text,
} from '../../../components';
import { Color, Style } from '../../../constants';
import { color } from '../../../constants/color.css';
import { useCompany } from '../../../contexts/ClientDataContext';
import { useAuthUser } from '../../../hooks';
import { isAgency } from '../../../reducers/auth';
import TicketBadge from '../../client-tickets/components/TicketBadge';
import { CommentsThread } from '../../comments';
import { labelForColumn } from '../board';
import CancelIdeaModal from './CancelIdeaModal';

export default function IdeaDetailPage() {
  const { companySlug, ideaId } = useParams<{
    companySlug: string;
    ideaId: string;
  }>();
  const company = useCompany(companySlug!);
  const authUser = useAuthUser();
  const showInternal = isAgency(authUser);

  const api = Api.WishlistIdea.useReadItem(ideaId!);
  const [confirmingCancel, setConfirmingCancel] = React.useState(false);

  if (api.error) return <PageError error={api.error} />;
  if (!api.data) return <PageLoading />;

  const idea = api.data;
  const isAuthor = idea.author?.id === authUser.id;
  const canCancel = isAgency(authUser) || (isAuthor && idea.state === 'idea');

  return (
    <PageWrap background={Color.neutral5} noPadding limitWidth="large">
      <Helmet>
        <title>{`${idea.title} | ${company.name}`}</title>
      </Helmet>

      <PageWrapPadding top horizontal bottom>
        <Stack gap={1.5}>
          <Stack gap={0.5}>
            <PageHeader
              title={
                <Stack
                  style={[Style.row, Style.alignCenter, Style.wrap]}
                  gap={0.5}
                >
                  <TicketBadge
                    label={labelForColumn(idea.state)}
                    bgColor={Color.blueGreyBg}
                    textColor={Color.blueGrey}
                    regular
                  />
                  <Text as="h1" typeface="headline" color="neutral100">
                    {idea.title}
                  </Text>
                </Stack>
              }
              back={{
                label: 'Wishlist',
                to: `/company/${companySlug}/wishlist`,
              }}
            />
            <Stack style={[Style.row, Style.alignCenter]} gap={0.5}>
              <Text typeface="body.small.regular" color="neutral60">
                Opened <DateWithTooltip timestamp={idea.createdAt} />
              </Text>
              <html.div style={styles.divider} />
              <Stack style={[Style.row, Style.alignCenter]} gap={0.25}>
                <ChatIcon size={16} color={color.neutral60} />
                <Text typeface="body.small.regular" color="neutral60">
                  {idea.comments.length}
                </Text>
              </Stack>
            </Stack>
          </Stack>

          <Stack style={[Style.row, styles.columns]} gap={1.5}>
            <Stack gap={1.5} style={[Style.fill, styles.timelineColumn]}>
              <CommentsThread
                entity={{
                  body: idea.body,
                  uploads: idea.uploads,
                  author: idea.author,
                  createdAt: idea.createdAt,
                  comments: idea.comments,
                }}
                showInternal={showInternal}
                invalidate={api.invalidate}
                composer={{
                  endpoint: '/comments',
                  relationKey: 'wishlistIdea',
                  relationValue: `/wishlist-ideas/${idea.id}`,
                }}
              />
            </Stack>

            <Stack gap={1.5} style={styles.sidebar}>
              <html.div style={styles.detailsCard}>
                <Stack gap={1}>
                  <Text typeface="body.medium" color="neutral100">
                    Idea details
                  </Text>
                  <DetailRow label="Author">
                    <Stack style={[Style.row, Style.alignCenter]} gap={0.5}>
                      <Avatar user={idea.author} size={24} />
                      <Text typeface="body.small.regular" color="neutral80">
                        {idea.author
                          ? `${idea.author.firstName} ${idea.author.lastName}`
                          : 'Unknown'}
                      </Text>
                    </Stack>
                  </DetailRow>
                  <DetailRow label="Created">
                    <Text typeface="body.small.regular" color="neutral80">
                      <DateWithTooltip timestamp={idea.createdAt} />
                    </Text>
                  </DetailRow>
                  <DetailRow label="Status">
                    <TicketBadge
                      label={labelForColumn(idea.state)}
                      bgColor={Color.blueGreyBg}
                      textColor={Color.blueGrey}
                    />
                  </DetailRow>
                  <DetailRow label="Service order">
                    <Text typeface="body.small.regular" color="neutral80">
                      {idea.serviceOrder
                        ? `#${
                            idea.serviceOrder.referenceNumber
                              ? `SO-${idea.serviceOrder.referenceNumber}`
                              : 'SO'
                          } ${idea.serviceOrder.name}`
                        : 'Not linked yet'}
                    </Text>
                  </DetailRow>
                </Stack>
              </html.div>

              {canCancel && (
                <html.div style={styles.detailsCard}>
                  <Stack gap={0.5}>
                    <Button
                      label="Cancel idea"
                      variant="text"
                      onClick={() => setConfirmingCancel(true)}
                    />
                    {!isAgency(authUser) && (
                      <Text typeface="body.small.regular" color="neutral60">
                        Only available while the idea is in Ideas.
                      </Text>
                    )}
                  </Stack>
                </html.div>
              )}
            </Stack>
          </Stack>
        </Stack>
      </PageWrapPadding>

      {confirmingCancel && (
        <CancelIdeaModal
          ideaId={idea.id}
          invalidate={api.invalidate}
          onClose={() => setConfirmingCancel(false)}
        />
      )}
    </PageWrap>
  );
}

function DetailRow({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <Stack gap={0.25}>
      <Text typeface="caption.medium" color="neutral60">
        {label}
      </Text>
      {children}
    </Stack>
  );
}

const styles = css.create({
  columns: {
    flexDirection: {
      default: 'row',
      '@media (max-width: 991px)': 'column',
    },
    alignItems: 'flex-start',
  },
  timelineColumn: {
    minWidth: 0,
  },
  sidebar: {
    width: {
      default: 350,
      '@media (max-width: 991px)': '100%',
    },
    flexShrink: 0,
  },
  detailsCard: {
    backgroundColor: color.neutral0,
    borderRadius: 12,
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: color.neutral20,
    padding: 16,
  },
  divider: {
    height: 20,
    width: 1,
    backgroundColor: color.neutral20,
  },
});
